import { Box, Button, TableCell, TableRow, Typography, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'

import { useRemoveAccountStepsConnections } from 'src/hooks'
import { useApproveStepAccountConnectionManually } from 'src/hooks/steps/use-approve-step-account-connection-manually'

import { HelperWrapper } from 'src/components/helper-wrapper'

import { ETranslationNs, IStepAccountRespDto } from 'src/types'
import { getTableTimestamp } from 'src/common/utils'

import { useStyles } from 'src/components/impacts-library/impacts-library-list/styles'

interface TableListItemProps {
  item: IStepAccountRespDto
  userTimezone?: string
}

enum EStepAccountState {
  Disconnected = 'disconnected',
  Active = 'active',
  Pending = 'pending',
}

const TableListItem = ({ item, userTimezone }: TableListItemProps) => {
  const [itemState, setItemState] = useState<EStepAccountState>(
    item.account_disconnected
      ? EStepAccountState.Disconnected
      : item.account_approved
        ? EStepAccountState.Active
        : EStepAccountState.Pending,
  )
  useEffect(() => {
    setItemState(
      item.account_disconnected
        ? EStepAccountState.Disconnected
        : item.account_approved
          ? EStepAccountState.Active
          : EStepAccountState.Pending,
    )
  }, [item])

  const theme = useTheme()
  const classes = useStyles()

  const { mutate: disconnect, isLoading } = useRemoveAccountStepsConnections()
  const { mutate: approveConnection, isLoading: isApproveConnectionLoading } =
    useApproveStepAccountConnectionManually()

  return (
    <TableRow key={item.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell align="center">{item.step?.account?.account_holder}</TableCell>

      <TableCell align="center">
        <p style={{ whiteSpace: 'pre-line' }}>
          {<strong>{item.step?.name}</strong>}
          {'\n'}#{item.step?.code}
        </p>
      </TableCell>
      <TableCell
        align="center"
        sx={{ minWidth: '165px', maxWidth: '300px', wordBreak: 'break-word' }}
      >
        {item?.step?.locations.map((el) => (
          <Typography sx={{ fontSize: 'inherit' }}>{el.location_name}</Typography>
        ))}
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <HelperWrapper
            t={
              itemState === EStepAccountState.Disconnected
                ? 'disconnected-status-text'
                : itemState === EStepAccountState.Active
                  ? 'active-status-text'
                  : 'pending-status-text'
            }
            ns={ETranslationNs.Common}
            isColorInherited
          />
          {item.requested_connection_date && (
            <div>
              <Typography
                sx={{
                  fontSize: '10px',
                  // whiteSpace: 'nowrap',
                }}
              >
                <HelperWrapper
                  t={'step-conection-table-requested-at-lable'}
                  ns={ETranslationNs.Common}
                />
              </Typography>
              <Typography
                sx={{
                  fontSize: '10px',
                  color: theme.palette.custom.grey2,
                  whiteSpace: 'nowrap',
                }}
              >
                {getTableTimestamp({
                  timezone: userTimezone,
                  date: item.requested_connection_date,
                })}
              </Typography>
            </div>
          )}
          {item.approved_connection_date && (
            <div>
              <Typography
                sx={{
                  fontSize: '10px',
                  // whiteSpace: 'nowrap',
                }}
              >
                <HelperWrapper
                  t={
                    itemState === EStepAccountState.Disconnected
                      ? 'step-conection-table-disconnected-at-lable'
                      : 'step-conection-table-approved-at-lable'
                  }
                  ns={ETranslationNs.Common}
                />
              </Typography>
              <Typography
                sx={{
                  fontSize: '10px',
                  color: theme.palette.custom.grey2,
                  whiteSpace: 'nowrap',
                }}
              >
                {getTableTimestamp({
                  timezone: userTimezone,
                  date: item.approved_connection_date,
                })}
              </Typography>
            </div>
          )}
        </Box>
      </TableCell>
      <TableCell align="right">
        <Button
          type="button"
          variant={
            itemState === EStepAccountState.Disconnected || itemState === EStepAccountState.Active
              ? 'outlined'
              : 'contained'
          }
          className={`${classes.impactsLibraryButton} ${
            itemState === EStepAccountState.Disconnected || itemState === EStepAccountState.Active
              ? classes.buttonOutlined
              : ''
          } ${itemState === EStepAccountState.Active ? classes.buttonDissconect : ''}`}
          onClick={() => {
            if (itemState === EStepAccountState.Active) {
              disconnect({
                stepId: item.step?.id || '',
                accountId: item.account?.id || '',
              })
              setItemState(EStepAccountState.Disconnected)
            } else {
              approveConnection(item.id)
              setItemState(EStepAccountState.Active)
            }
          }}
          disabled={isLoading || isApproveConnectionLoading}
        >
          <HelperWrapper
            t={
              itemState === EStepAccountState.Disconnected
                ? 'reconnect-button-text'
                : itemState === EStepAccountState.Active
                  ? 'disconnect-button-text'
                  : 'approve-button-text'
            }
            isColorInherited
          />
        </Button>
      </TableCell>
    </TableRow>
  )
}

export default TableListItem
